import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Settings } from 'lucide-react';
import { ProfileAvatar } from '@/components/ProfileAvatar';
import { useAuth } from '@/features/auth/AuthContext';

export function ProfilesPage() {
  const { user, isLoading, profiles, switchProfile } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState<string | null>(null);
  const next = params.get('next') || '/browse';

  if (isLoading) return null;
  if (!user) return <Navigate to="/login" replace />;

  async function pick(id: string) {
    setBusy(id);
    try {
      await switchProfile(id);
      navigate(next);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="grid min-h-[70vh] place-items-center px-4 py-16">
      <div className="w-full max-w-3xl text-center">
        <h1 className="font-display text-3xl font-extrabold text-ink md:text-4xl">Who's watching?</h1>
        <p className="mt-2 text-ink-muted">Pick a profile to keep lists and history separate.</p>

        <div className="mt-10 flex flex-wrap items-start justify-center gap-6">
          {profiles.map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={busy !== null}
              onClick={() => pick(p.id)}
              className="group flex w-28 flex-col items-center gap-3 disabled:opacity-60"
            >
              <ProfileAvatar
                profile={p}
                size={104}
                className="ring-2 ring-transparent transition group-hover:ring-brand group-hover:brightness-110"
              />
              <span className="truncate text-sm font-medium text-ink-muted group-hover:text-ink">
                {p.name}
                {p.isKids && <span className="ml-1 rounded bg-surface-raised px-1.5 py-0.5 text-2xs text-ink-faint">Kids</span>}
              </span>
            </button>
          ))}

          <button
            type="button"
            onClick={() => navigate('/account?section=profiles')}
            className="flex w-28 flex-col items-center gap-3 text-ink-faint hover:text-ink"
          >
            <span className="grid h-[104px] w-[104px] place-items-center rounded-xl border-2 border-dashed border-border transition hover:border-brand">
              <Plus size={36} strokeWidth={1.5} />
            </span>
            <span className="text-sm font-medium">Add profile</span>
          </button>
        </div>

        <button
          type="button"
          onClick={() => navigate('/account?section=profiles')}
          className="mt-12 inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-ink-muted hover:text-ink"
        >
          <Settings size={16} /> Manage profiles
        </button>
      </div>
    </div>
  );
}
